import React, { useState, useRef, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
} from "reactstrap";
import _ from "lodash";
import { useFetch2 } from "components/utils/authenticatedHook";
import encryptRequest from "../../../helpers/encryptationHelper";
import { isValidURL } from "../../../constants/general.constant";

function CreateEducationMaterialModal(props) {
  const [{ response, error, isLoading }, doFetch, sendError] = useFetch2();
  const inputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [formError, setFormError] = useState({
    materialLink: [] // array of error messages per index
  }); const [formSubmitted, setFormSubmitted] = useState(false);
  const [editFiles, setEditFiles] = useState(props?.editData ? props?.editData?.files : []);
  const [formData, setFormData] = useState({
    materialType: props?.editData && props?.editData?.materialType ? props?.editData?.materialType : '',
    note: props?.editData && props?.editData?.note ? props?.editData?.note : '',
    materialLink: props?.editData && props?.editData?.materialLink && props?.editData?.materialLink.length > 0 ? props?.editData?.materialLink : [""],
  });

  const handleIconClick = () => {
    const event = new MouseEvent("click", {
      bubbles: true,
      cancelable: true,
    });
    inputRef.current.dispatchEvent(event); // Manually dispatch the click event
  };

  const handleFileChange = (event) => {
    if (selectedFiles && selectedFiles.length > 5) {
      setFormError({
        type: "educationDocuments",
        message: "You can select up to 5 educational documents",
      });
      return;
    }
    const files = Array.from(event.target.files);
    if (files.length > 5) {
      setFormError({
        type: "educationDocuments",
        message: "You can select up to 5 educational documents",
      });
      return;
    }
    const maxSize = 20 * 1024 * 1024;
    const oversizedFile = files.find(file => file.size > maxSize);

    if (oversizedFile) {
      setFormError({
        type: "educationDocuments",
        message: `Each file must be less than 20MB. "${oversizedFile.name}" is too large.`,
      });
      return;
    }
    setSelectedFiles(prevFiles => {
      // Merge and remove duplicates based on 'name' and 'lastModified'
      const mergedFiles = [...prevFiles, ...files];

      const uniqueFiles = mergedFiles.filter(
        (file, index, self) =>
          index === self.findIndex(
            f => f.name === file.name && f.lastModified === file.lastModified
          )
      );

      return uniqueFiles;
    });
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name == 'materialType' && _.isEmpty(value) && formSubmitted) {
      setFormError({
        type: "materialType",
        message: "Please add material type",
      });
      return;
    }
    if (name == 'materialType' && _.size(value) < 3 && formSubmitted) {
      setFormError({
        type: "materialType",
        message: "The material type must be 3 characters",
      });
      return;
    }
    if (name == 'note' && _.isEmpty(value) && formSubmitted) {
      setFormError({ type: "note", message: "Please add note" });
      return;
    }
    if (name == 'note' && _.size(value) < 5 && formSubmitted) {
      setFormError({
        type: "note",
        message: "The note must be 5 characters",
      });
      return;
    }
    setFormError({});
  }
  const removeFile = (indexToRemove) => {
    const updatedFiles = selectedFiles.filter(
      (_, index) => index !== indexToRemove
    );
    setSelectedFiles(updatedFiles);
  };

  const removeEditFile = (indexToRemove) => {
    const updatedFiles = editFiles.filter(
      (_, index) => index !== indexToRemove
    );
    setEditFiles(updatedFiles)
  };
  const handleMaterialTypeChange = (index, value) => {
    const updatedLinks = [...formData.materialLink];
    updatedLinks[index] = value;
    setFormData({ ...formData, materialLink: updatedLinks });
    const updatedErrors = [...(formError.materialLink || [])];

    if (!isValidURL(value)) {
      updatedErrors[index] = "Please enter a valid URL";
    } else {
      updatedErrors[index] = ""; // clear error if valid
    }

    setFormData({ ...formData, materialLink: updatedLinks });
    setFormError({ ...formError, materialLink: updatedErrors });
  };

  const handleAddLink = () => {
    if (formData.materialLink.length < 5) {
      setFormData({ ...formData, materialLink: [...formData.materialLink, ""] });
    }
  };

  const handleRemoveLink = (index) => {
    const updatedLinks = formData.materialLink.filter((_, i) => i !== index);
    setFormData({ ...formData, materialLink: updatedLinks });
  };

  function onSubmit(event) {
    event.preventDefault();
    const form = event.target;
    const elements = form && form.elements ? form.elements : null;
    if (elements) {
      const materialType = elements.materialType.value;
      const note = elements.note.value;
      if (_.isEmpty(materialType)) {
        setFormError({
          type: "materialType",
          message: "Please add material type",
        });
        return;
      }
      if (_.size(materialType) < 3) {
        setFormError({
          type: "materialType",
          message: "The material type must be 3 characters",
        });
        return;
      }
      // edit files validation logic
      if (props.editData && Object.keys(props.editData).length > 0) {
        if (editFiles.length === 0 && selectedFiles.length === 0) {
          setFormError({
            type: "educationDocuments",
            message: "Please select atleast 1 eduaction document",
          });
          return;
        }
      } else {
        if (selectedFiles.length === 0) {
          setFormError({
            type: "educationDocuments",
            message: "Please select atleast 1 eduaction document",
          });
          return;
        }
      }
      if (selectedFiles && editFiles && ((selectedFiles.length + editFiles.length) > 5)) {
        setFormError({
          type: "educationDocuments",
          message: "You can select up to 5 educational documents",
        });
        return;
      }
      if (_.isEmpty(note)) {
        setFormError({ type: "note", message: "Please add note" });
        return;
      }
      if (_.size(note) < 5) {
        setFormError({
          type: "note",
          message: "The note must be 5 characters",
        });
        return;
      }
      let cleanedLinks = [];

      if (formData.materialLink) {
        cleanedLinks = formData.materialLink
          .filter(link => link && link.trim() !== "");
        // Validate links
        const errors = cleanedLinks.map(link =>
          isValidURL(link) ? "" : "Please enter a valid URL"
        );
        setFormData({ ...formData, materialLink: cleanedLinks });
        // Set validation errors if any
        setFormError({ ...formError, materialLink: errors });
        // Return true if all links are valid
        if (!errors.every(error => error === "")) {
          return
        }
      }
      setFormSubmitted(true);
      setFormError({});

      if (props.editData && Object.keys(props.editData).length > 0) {
        const payload = {
          note: note,
          materialType: materialType,
          editFiles: editFiles,
        };
        if (selectedFiles.length > 0) {
          payload.files = selectedFiles;
        }
        if (cleanedLinks.length > 0) {
          payload.materialLink = cleanedLinks
        }
        doFetch({
          url: "/update-education-material/" + encryptRequest(props?.editData?._id),
          method: "PUT",
          disableWait: true,
          body: payload,
          isFormData: selectedFiles.length > 0 ? true : false,
        });
      } else {
        const payload = {
          note: note,
          materialType: materialType,
          files: selectedFiles,
        };
        if (cleanedLinks.length > 0) {
          payload.materialLink = cleanedLinks
        }
        doFetch({
          url: "/create-education-material",
          method: "POST",
          disableWait: true,
          body: payload,
          isFormData: true,
        });
      }
    }
  }
  useEffect(() => {
    if (!isLoading && formSubmitted) {
      setFormSubmitted(false);
      props.toggleModal("reload");
    }
  }, [isLoading]);
  return (
    <div>
      <Modal
        isOpen={props.addModal}
        toggle={props.toggleModal}
        backdrop="static"
        className="bastaLang modal-md"
      >
        <ModalHeader toggle={props.toggleModal}>
          Create a Educational Material
        </ModalHeader>

        <ModalBody style={{ backgroundColor: "white" }}>
          <Form onSubmit={onSubmit}>
            <FormGroup>
              <Label for="materialType">Educational Material Type *</Label>
              <Input type="text" name="materialType" rows={10} value={formData.materialType} onChange={handleChange} className="form-control input-date-filter remove-bottom-margin"/>
              {formError &&
                formError.type == "materialType" &&
                formError.message && (
                  <Label
                    className="error-message"
                    style={{
                      color: "red",
                      textAlign: "left",
                      paddingTop: "3px",
                    }}
                  >
                    {formError.message}
                  </Label>
                )}
            </FormGroup>
            <FormGroup>
              <Label for="educationDocuments">
                Select Educational Documents *
              </Label>
              {/* Hidden file input */}

              <input
                type="file"
                name="educationDocuments"
                rows={10}
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.webp,.bmp"
                onChange={handleFileChange}
                style={{ display: "none" }}
                ref={inputRef}
              />
              {/* Visible clickable icon/button */}
              <div>
                <div
                  style={{
                    border: "2px dashed #3399ff",
                    borderRadius: 8,
                    padding: 17,
                    textAlign: "center",
                    cursor: "pointer",
                  }}
                  onClick={handleIconClick}
                >
                  <div style={{ fontSize: 24, color: "#3399ff" }}>+</div>
                  <div style={{ color: "#3399ff", fontWeight: 500 }}>Browse</div>
                </div>
              </div>
              {formError &&
                formError.type == "educationDocuments" &&
                formError.message && (
                  <Label
                    style={{
                      color: "red",
                      textAlign: "left",
                      paddingTop: "3px",
                    }}
                  >
                    {formError.message}
                  </Label> // Display error if validation fails
                )}
            </FormGroup>
            {/* Display selected files */}
            {((selectedFiles.length > 0) || (editFiles && editFiles.length > 0)) && <div style={{ marginBottom: 20, marginTop: 15 }}>
              <strong>Documents</strong>
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: "#f5f5f5",
                    marginTop: 8,
                    padding: 10,
                    borderRadius: 6,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {file.name}
                  </span>

                  <button
                    onClick={() => removeFile(index)}
                    style={{
                      background: "transparent",
                      border: "none",
                      fontSize: 16,
                      color: "#ff4d4f",
                      cursor: "pointer",
                    }}
                    type="button"
                  >
                    ×
                  </button>
                </div>
              ))}
              {/* edit files display */}
              {editFiles && editFiles.length > 0 && editFiles.map((file, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: "#f5f5f5",
                    marginTop: 8,
                    padding: 10,
                    borderRadius: 6,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {file.name}
                  </span>
                  <button
                    onClick={() => removeEditFile(index)}
                    style={{
                      background: "transparent",
                      border: "none",
                      fontSize: 16,
                      color: "#ff4d4f",
                      cursor: "pointer",
                    }}
                    type="button"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>}

            {/* add attach the documnet link */}
            <FormGroup>
              <Label>Educational Material Link</Label>
              {formData.materialLink && formData.materialLink.map((link, index) => (
                <div>
                  <div key={index} style={{ display: "flex", alignItems: "center", marginBottom: "8px" }} className="w-100">
                    <Input
                      type="text"
                      value={link}
                      onChange={(e) => handleMaterialTypeChange(index, e.target.value)}
                      name="materialLink"
                      className="form-control input-date-filter remove-bottom-margin"
                    />
                    {formData.materialLink.length < 5 && index === formData.materialLink.length - 1 && (
                      <button type="button" className="btn btn-sm mt-1" onClick={handleAddLink} style={{ marginLeft: "8px", borderRadius: '8px', background: 'rgb(76, 175, 80)', color: 'white', height: "40px" }}>+</button>
                    )}
                    {formData.materialLink.length > 1 && (
                      <button type="button" className="btn btn-sm mt-1" onClick={() => handleRemoveLink(index)} style={{ marginLeft: "4px", color: "red", borderRadius: '8px', background: 'rgb(221, 51, 51)', color: 'white', height: '40px' }}>-</button>
                    )}
                  </div>
                  {formError.materialLink?.[index] && (
                    <div style={{ color: 'red', marginTop: '4px' }}>
                      {formError.materialLink[index]}
                    </div>
                  )}
                </div>
              ))}
            </FormGroup>

            <FormGroup>
              <Label for="note">Note *</Label>
              <Input
                type="textarea"
                className="input-date-filter remove-bottom-margin mb-3"
                name="note"
                rows={3}
                style={{ lineHeight: "1.4", fontSize: "17px" }}
                onChange={handleChange}
                value={formData.note}
              />
              {formError &&
                formError.type == "note" &&
                formError.message && (
                  <Label
                    style={{
                      color: "red",
                      textAlign: "left",
                      marginTop: "-7px",
                    }}
                  >
                    {formError.message}
                  </Label> // Display error if validation fails
                )}
            </FormGroup>
            <button
              type="submit"
              className="btn btn-block btn-info"
              disabled={isLoading}
            >
              {isLoading ? (
                <i
                  className="mdi mdi-loading mdi-spin mr-2"
                  style={{ fontSize: 17 }}
                ></i>
              ) : (
                <i
                  className="mdi mdi-content-save mr-2"
                  style={{ fontSize: 17 }}
                ></i>
              )}
              {props?.editData ? 'UPDATE' : 'SAVE'}
            </button>
          </Form>
        </ModalBody>
      </Modal>
    </div>
  );
}

export default CreateEducationMaterialModal;
